// Location: src/components/admin/analytics/components/RecentConversionsCard.tsx
// Purpose: Reusable component for displaying recent affiliate conversions with pagination
// Scope: Used in affiliate analytics views to show all recent conversions

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Calendar, Mail, Target, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

interface RecentConversion {
	_id: string;
	conversionType: string;
	conversionEmail?: string;
	conversionDate: string;
	utmSource?: string;
	utmCampaign?: string;
	landingPage: string;
	latestAttributionUpdate?: string;
	latestAttributionGameId?: string;
}

interface RecentConversionsCardProps {
	affiliateAnalytics: any | undefined;
	formatNumber: (num: number | undefined) => string;
}

export function RecentConversionsCard({
	affiliateAnalytics,
	formatNumber,
}: RecentConversionsCardProps) {
	const [currentPage, setCurrentPage] = useState(1);
	const [isAnimating, setIsAnimating] = useState(false);
	const itemsPerPage = 8;

	// All recent conversions
	const conversions = useMemo(() => {
		if (!affiliateAnalytics?.recentConversions) return [];
		return affiliateAnalytics.recentConversions;
	}, [affiliateAnalytics]);

	const totalPages = Math.ceil(conversions.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const currentPageData = conversions.slice(startIndex, endIndex);

	const handlePageChange = async (newPage: number) => {
		if (newPage === currentPage || isAnimating) return;

		setIsAnimating(true);
		await new Promise((resolve) => setTimeout(resolve, 150)); // Short delay for animation
		setCurrentPage(newPage);
		setTimeout(() => setIsAnimating(false), 150);
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle className='flex items-center justify-between'>
					<div className='flex items-center gap-2'>
						<Calendar className='h-5 w-5' />
						Recent Conversions
					</div>
					<Badge
						variant='secondary'
						className='bg-gray-500/10 text-gray-600 border-gray-500/20'
					>
						{formatNumber(conversions.length)} total
					</Badge>
				</CardTitle>
			</CardHeader>
			<CardContent>
				{conversions.length > 0 ? (
					<div className='space-y-4'>
						{/* Conversions list with animation */}
						<div
							className={cn(
								'space-y-3 transition-all duration-300 ease-in-out',
								isAnimating
									? 'opacity-0 transform translate-y-2'
									: 'opacity-100 transform translate-y-0'
							)}
						>
							{currentPageData.map(
								(
									conversion: RecentConversion,
									index: number
								) => (
									<div
										key={`${conversion._id}-${currentPage}`}
										className={cn(
											'flex items-center justify-between p-4 bg-white/50 dark:bg-gray-900/50 rounded-lg border border-gray-200/50 dark:border-gray-800/30 transition-all duration-300 ease-out hover:bg-gray-50/50 dark:hover:bg-gray-800/20 hover:border-gray-300/50 hover:shadow-md',
											'animate-in fade-in slide-in-from-bottom-2'
										)}
										style={{
											animationDelay: `${index * 50}ms`,
											animationFillMode: 'both',
										}}
									>
										<div className='flex items-center gap-3'>
											<div
												className={cn(
													'h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0',
													conversion.conversionType ===
														'email_signup'
														? 'bg-gradient-to-r from-emerald-500 to-green-600'
														: 'bg-gradient-to-r from-pink-500 to-purple-600'
												)}
											>
												{conversion.conversionType ===
												'email_signup' ? (
													<Mail className='h-4 w-4 text-white' />
												) : (
													<Target className='h-4 w-4 text-white' />
												)}
											</div>
											<div className='min-w-0'>
												<div className='font-medium text-gray-700 dark:text-gray-300 truncate'>
													{conversion.conversionType ===
													'email_signup'
														? 'Email Lead'
														: 'New Watcher'}
												</div>
												<div className='text-sm text-muted-foreground'>
													{conversion.conversionEmail ||
														'User registration'}
													{conversion.utmSource &&
														` • Source: ${conversion.utmSource}`}
													{conversion.utmCampaign &&
														` • Campaign: ${conversion.utmCampaign}`}
												</div>
												<div className='text-xs text-muted-foreground'>
													{format(
														new Date(
															conversion.conversionDate
														),
														'MMM d, yyyy h:mm a'
													)}
												</div>
											</div>
										</div>
										<div className='text-right flex-shrink-0'>
											<Badge
												variant={
													conversion.conversionType ===
													'email_signup'
														? 'secondary'
														: 'default'
												}
												className={
													conversion.conversionType ===
													'email_signup'
														? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
														: 'bg-pink-500/10 text-pink-600 border-pink-500/20'
												}
											>
												{conversion.conversionType ===
												'email_signup'
													? 'Email Lead'
													: 'New Watcher'}
											</Badge>
											<div className='text-sm text-muted-foreground mt-1'>
												{conversion.conversionType ===
												'email_signup'
													? '10 KARMA'
													: '100 KARMA'}
											</div>
										</div>
									</div>
								)
							)}
						</div>

						{/* Beautiful Pagination */}
						{totalPages > 1 && (
							<div className='flex items-center justify-between pt-4 border-t border-gray-200/30'>
								<div className='text-sm text-muted-foreground'>
									Showing {startIndex + 1}-
									{Math.min(endIndex, conversions.length)} of{' '}
									{conversions.length} conversions
								</div>

								<div className='flex items-center gap-2'>
									<Button
										variant='outline'
										size='sm'
										onClick={() =>
											handlePageChange(currentPage - 1)
										}
										disabled={
											currentPage === 1 || isAnimating
										}
										className={cn(
											'h-8 w-8 p-0 border-gray-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 transition-all duration-200',
											currentPage === 1
												? 'cursor-not-allowed'
												: 'hover:shadow-sm'
										)}
									>
										<ChevronLeft className='h-4 w-4' />
									</Button>

									{/* Page numbers */}
									<div className='flex items-center gap-1'>
										{Array.from(
											{ length: totalPages },
											(_, i) => i + 1
										).map((pageNum) => {
											const isActive =
												pageNum === currentPage;
											const isNear =
												Math.abs(
													pageNum - currentPage
												) <= 1;
											const isFirst = pageNum === 1;
											const isLast =
												pageNum === totalPages;

											if (
												!isNear &&
												!isFirst &&
												!isLast &&
												totalPages > 5
											) {
												if (
													pageNum ===
														currentPage - 2 ||
													pageNum === currentPage + 2
												) {
													return (
														<span
															key={pageNum}
															className='text-muted-foreground text-sm'
														>
															...
														</span>
													);
												}
												return null;
											}

											return (
												<Button
													key={pageNum}
													variant={
														isActive
															? 'default'
															: 'outline'
													}
													size='sm'
													onClick={() =>
														handlePageChange(
															pageNum
														)
													}
													disabled={isAnimating}
													className={cn(
														'h-8 w-8 p-0 transition-all duration-200',
														isActive
															? 'bg-gradient-to-r from-gray-600 to-gray-700 text-white border-transparent shadow-md hover:shadow-lg'
															: 'border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
													)}
												>
													{pageNum}
												</Button>
											);
										})}
									</div>

									<Button
										variant='outline'
										size='sm'
										onClick={() =>
											handlePageChange(currentPage + 1)
										}
										disabled={
											currentPage === totalPages ||
											isAnimating
										}
										className={cn(
											'h-8 w-8 p-0 border-gray-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 transition-all duration-200',
											currentPage === totalPages
												? 'cursor-not-allowed'
												: 'hover:shadow-sm'
										)}
									>
										<ChevronRight className='h-4 w-4' />
									</Button>
								</div>
							</div>
						)}
					</div>
				) : (
					<div className='text-center text-muted-foreground py-12'>
						<Calendar className='h-12 w-12 mx-auto mb-4 text-gray-400/50' />
						<div className='text-lg font-medium mb-2'>
							No Conversions Yet
						</div>
						<div className='text-sm'>
							Your conversions will appear here as they happen
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}